import React from 'react';
import PropTypes from 'prop-types';
import { Loader } from 'semantic-ui-react';
import SelectableImage from './components/selectableImage';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmModal from '../../components/confirmModal';
import { ToastContext, Card, Icon } from '@weezevent/nacre';
import ImagesModel from '../../models/image';
import FilesModel from '../../models/file';
import LineImage from '../../pages/minisite/upload/lineImage';
import './components.gallery.scss';

const OrganizationGallery = ({ onSelected, organization, isBanner, onClick, fileType = 'image' }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const model = fileType === 'image' ? ImagesModel : FilesModel;
    const [files, loadFiles, syncFiles] = model.useApiModel({ cache: false, organizationId: organization.id });
    const [deleteId, setDeleteId] = React.useState(null);

    const closeModal = React.useCallback(() => {
        setDeleteId(null);
    }, [setDeleteId]);

    const onValidated = React.useCallback(() => {
        const fileToDelete = new model({ id: deleteId });
        fileToDelete
            .delete({
                organizationId: organization.id
            })
            .then(() => {
                toast.success(t(`ticket.event.minisite.image-delete`));
                syncFiles();
                closeModal();
            });
    }, [deleteId, organization, files]);

    if (loadFiles) {
        return (
            <div className="wz-gallery-loading">
                <Loader active />
            </div>
        );
    }

    if (!files || files.length === 0) {
        return (
            <div className="wz-empty-container">
                {isBanner ? (
                    <Card.EmptyState
                        icon={<Icon name="list" />}
                        title={<Trans i18nKey={`ticket.event.minisite.recent-${fileType}-banner-title`} />}
                        subtitle={<Trans i18nKey={`ticket.event.minisite.recent-${fileType}-banner-text`} />}
                        labelButton={<Trans i18nKey={`ticket.event.minisite.recent-${fileType}-banner-cta`} />}
                        onClick={onClick}
                    />
                ) : (
                    <Card.EmptyState
                        icon={<Icon name="list" />}
                        title={<Trans i18nKey={`ticket.event.minisite.recent-${fileType}-title`} />}
                        subtitle={<Trans i18nKey={`ticket.event.minisite.recent-${fileType}-text`} />}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            <div className="wz-recent-images">
                <Trans i18nKey={`ticket.event.minisite.recent-${fileType}`} />
            </div>
            <div className="wz-gallery-container">
                <ConfirmModal
                    onClose={closeModal}
                    onAccept={onValidated}
                    objectToSave={Boolean(deleteId)}
                    labelTitle={t('ticket.event.minisite.gallery.delete-image-title')}
                    labelBody={t('ticket.event.minisite.gallery.delete-image-description')}
                />
                {fileType === 'image' && (
                    <div className="wz-gallery-container-images">
                        {files.map((file, key) => (
                            <SelectableImage
                                key={key}
                                url={file.url}
                                onSelected={url => {
                                    onSelected(url);
                                }}
                                onDelete={() => setDeleteId(file.id)}
                                size={file.size}
                                isBanner={Boolean(isBanner)}
                            />
                        ))}
                    </div>
                )}
                {fileType === 'file' && (
                    <div className={'wz-gallery-container-files'}>
                        {files.map((file, key) => (
                            <LineImage key={key} src={file.name} name={file.name} onClick={() => onSelected({ url: file.url, name: null })} />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

OrganizationGallery.propTypes = {
    organization: PropTypes.object.isRequired,
    onSelected: PropTypes.func.isRequired,
    isBanner: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired
};

export default OrganizationGallery;
